import useIntl from '@/utils/intl'
import React, { FunctionComponent, useEffect, useReducer, useRef } from 'react'
import { Obj } from '..'
import { useMobiApi } from '@/services/mobi-api'
import {
  Categories,
  ChoosenInfo,
  GetPackageInfo,
  PackageDetailInfo,
  SavePackageInfoReq,
  SubCategories,
  serviceDetail
} from '@/services/mobi-api/numberv2/types'
import Loader from '@/components/ui/loader'
import { Checkbox, MenuItem, TextField } from '@mui/material'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import classNames from 'classnames'
import { CheckCircle, RadioButtonUncheckedOutlined, ExpandMore, ExpandLess } from '@mui/icons-material'
import { useToast } from '@/context/ToastContext'
import { useSearchParams } from 'next/navigation'
import { useAuth } from '@/context/AuthContext'
import { Locale, useLanguage } from '@/context/LanguageContext'
// import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { moneyFormatter } from '@/utils/helpers'
import { resolveImageUrl } from '@/utils/constants'
import { useTheme } from '@/context/ThemeContext'
import Image from 'next/image'
// import { createPortal } from 'react-dom'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

interface Props {
  next: (obj: Obj) => void
  back: () => void
  instanceId: string
  referralCode?: string
  onSelectionChange?: (info: ChoosenInfo) => void
}

interface State {
  package?: GetPackageInfo
  loading?: boolean
  choosenCategory?: Categories
  choosenSubCat?: SubCategories
  choosenCallMM?: serviceDetail
  choosenDataMM?: serviceDetail
  choosenSmsMM?: serviceDetail
  choosenEntertainmentMM?: serviceDetail[]
  choosenVideoStreamingMM?: serviceDetail
  choosenPackage?: PackageDetailInfo
  lang?: string
  showCustomPackages?: boolean
}

type CategoryKey = 'postpaid' | 'prepaid' | 'hybrid' | 'unknown'

const CATEGORY_DISPLAY_ORDER: Record<CategoryKey, number> = {
  postpaid: 0,
  prepaid: 1,
  hybrid: 2,
  unknown: Number.MAX_SAFE_INTEGER
}

const getCategoryKey = (
  category: Pick<Categories, 'categoryId' | 'categoryName'>
): CategoryKey => {
  switch (category.categoryId) {
    case 'f0540b81-550d-4c2c-908e-6976eeb2e84d':
      return 'postpaid'
    case '47193d9a-3361-4488-8c07-07bf6d315ad7':
      return 'prepaid'
    case '4aa05809-6d33-4aa1-8f15-0b9538d8bdc1':
      return 'hybrid'
    default: {
      const normalizedName = category.categoryName.trim().toLowerCase()

      if (normalizedName === 'postpaid' || normalizedName === 'дараа төлбөрт') {
        return 'postpaid'
      }

      if (
        normalizedName === 'prepaid' ||
        normalizedName === 'урьдчилсан төлбөрт'
      ) {
        return 'prepaid'
      }

      if (
        normalizedName === 'hybrid' ||
        normalizedName === 'хосолсон төлбөрт'
      ) {
        return 'hybrid'
      }

      return 'unknown'
    }
  }
}

const ChoosePackage: FunctionComponent<Props> = ({
  next,
  back,
  instanceId,
  referralCode,
  onSelectionChange
}) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { numberv2 } = useMobiApi()
  const { isWebView } = useAuth()
  const { locale, setLocale } = useLanguage()
  const searchParams = useSearchParams()
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {}
  )
  const lang = searchParams.get('lang')
  const [selectedAddonTab, setSelectedAddonTab] = React.useState(0)
  const [selected, setSelected] = React.useState<any>(null)
  const { theme } = useTheme()
  const categoryScrollRef = useRef<HTMLDivElement>(null)
  const isCategoryDraggingRef = useRef(false)
  const categoryDragStartXRef = useRef(0)
  const categoryDragStartScrollLeftRef = useRef(0)
  const categoryDragMovedRef = useRef(false)
  const orderedCategories = [...(state.package?.categories ?? [])].sort(
    (a, b) =>
      CATEGORY_DISPLAY_ORDER[getCategoryKey(a)] -
      CATEGORY_DISPLAY_ORDER[getCategoryKey(b)]
  )

  const getCategoryLabel = (
    category: Pick<Categories, 'categoryId' | 'categoryName'>
  ): string => {
    switch (getCategoryKey(category)) {
      case 'postpaid':
        return translate('login.postPaid')
      case 'hybrid':
        return translate('login.hybrid')
      case 'prepaid':
        return translate('login.prePaid')
      default:
        return category.categoryName
    }
  }

  const starsPackageLabel = locale === 'mn' ? 'багц' : 'package'
  const getStarsPackageSummary = (packageName?: string): string => {
    const resolvedName = packageName?.trim() ?? 'Stars'
    return `${resolvedName} ${starsPackageLabel}`
  }
  const getStarsCardData = (pkg: PackageDetailInfo): {
    imageSrc: string
    description: string
    features: string[]
  } => ({
    imageSrc: pkg.img?.trim() !== '' ? pkg.img ?? '/images/stars_family.png' : '/images/stars_family.png',
    description: pkg.description?.trim() !== '' ? pkg.description ?? translate('family.stars') : translate('family.stars'),
    features:
      pkg.services
        ?.map((service) => service.name?.trim())
        .filter((serviceName): serviceName is string => !!serviceName) ?? []
  })

  const handleSubCategoryClick = (
    subcategory: SubCategories,
    options?: { autoSelectSinglePackage?: boolean }
  ): void => {
    const autoSelectSinglePackage = options?.autoSelectSinglePackage ?? true

    trackNewNumberEvent('select_package', {
      step_name: 'registration_info',
      sub_step_name: 'package',
      package_type: normalizeTrackingText(subcategory.name)
    })

    const reset = {
      choosenSubCat: subcategory,
      choosenPackage: undefined,
      choosenCallMM: undefined,
      choosenSmsMM: undefined,
      choosenDataMM: undefined,
      choosenVideoStreamingMM: undefined,
      choosenEntertainmentMM: []
    }

    if (subcategory.id === '24aa6cc9-71cf-49ac-9c5b-136c93b1f147') {
      const defaultHybrid = subcategory.packages.find(p => !p.isAdditionalService)
      if (defaultHybrid != null) {
        setState({ ...reset, choosenPackage: defaultHybrid })
        onSelectionChange?.({
          choosenPackage: defaultHybrid,
          choosenCallMM: undefined,
          choosenDataMM: undefined,
          choosenSmsMM: undefined,
          choosenVideoStreamingMM: undefined,
          choosenEntertainmentMM: []
        })
        return
      }
    }

    if (autoSelectSinglePackage && subcategory.packages?.length === 1) {
      const defaultPackage = subcategory.packages[0]
      setState({ ...reset, choosenPackage: defaultPackage })
      onSelectionChange?.({
        choosenPackage: defaultPackage,
        choosenCallMM: undefined,
        choosenDataMM: undefined,
        choosenSmsMM: undefined,
        choosenVideoStreamingMM: undefined,
        choosenEntertainmentMM: []
      })
      return
    }

    setState(reset)
    onSelectionChange?.({})
  }

  const handleCategoryClick = (category: Categories): void => {
    if (categoryDragMovedRef.current) {
      categoryDragMovedRef.current = false
      return
    }

    trackNewNumberEvent('select_package', {
      step_name: 'registration_info',
      sub_step_name: 'package',
      package_type: normalizeTrackingText(category.categoryName)
    })

    const resetCategoryState = {
      choosenCategory: category,
      choosenSubCat: undefined,
      choosenPackage: undefined,
      choosenCallMM: undefined,
      choosenSmsMM: undefined,
      choosenDataMM: undefined,
      choosenVideoStreamingMM: undefined,
      choosenEntertainmentMM: []
    }

    if (getCategoryKey(category) === 'prepaid') {
      setState(resetCategoryState)
      onSelectionChange?.({})
      return
    }

    if (category.subCategories.length === 1) {
      const [singleSubCategory] = category.subCategories
      setState({ choosenCategory: category })
      handleSubCategoryClick(singleSubCategory, {
        autoSelectSinglePackage: getCategoryKey(category) !== 'prepaid'
      })
      return
    }

    setState(resetCategoryState)
    onSelectionChange?.({})
  }

  const handleCategoryMouseDown = (
    e: React.MouseEvent<HTMLDivElement>
  ): void => {
    if (categoryScrollRef.current == null) return

    isCategoryDraggingRef.current = true
    categoryDragMovedRef.current = false
    categoryDragStartXRef.current = e.clientX
    categoryDragStartScrollLeftRef.current = categoryScrollRef.current.scrollLeft
  }

  const handleCategoryMouseMove = (
    e: React.MouseEvent<HTMLDivElement>
  ): void => {
    if (!isCategoryDraggingRef.current || categoryScrollRef.current == null) return

    const deltaX = e.clientX - categoryDragStartXRef.current
    if (Math.abs(deltaX) > 4) {
      categoryDragMovedRef.current = true
    }

    categoryScrollRef.current.scrollLeft =
      categoryDragStartScrollLeftRef.current - deltaX
  }

  const stopCategoryDragging = (): void => {
    isCategoryDraggingRef.current = false
  }

  const addonPackages = state.package?.categories
    .flatMap((cat) => cat.subCategories)
    .filter((sub) => sub.selectedType === 'SELECT_SERVICE')
    .flatMap((sub) => sub.packages)
    .filter((pkg) => pkg.isAdditionalService === true)

  useEffect(() => {
    if (lang !== null && lang !== '') {
      setLocale(lang as Locale)
    }
    // return () => {
    //   controller.abort()
    // }
  }, [lang])

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const controller = new AbortController()
    void fetchPackage(locale, controller)
    return () => {
      controller.abort()
    }
  }, [locale])

  const fetchPackage = async (
    lang: string,
    controller: AbortController
  ): Promise<void> => {
    try {
      setState({ loading: true })
      const res = await numberv2.getPackageInfo(lang, controller.signal)
      setState({ loading: false })
      if (!controller.signal.aborted) {
        if (res !== undefined && res.error === undefined) {
          setState({ package: res })
        } else {
          trackNewNumberEvent('step_error', {
            step_name: 'registration_info',
            sub_step_name: 'package',
            error_type: normalizeTrackingText(res?.error ?? 'package_fetch_failed')
          })
          showError(translate('request.failed'))
        }
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        trackNewNumberEvent('step_error', {
          step_name: 'registration_info',
          sub_step_name: 'package',
          error_type: 'package_fetch_failed'
        })
        setState({ loading: false })
        showError(translate('request.failed'))
        console.error(e)
      }
    }
  }

  const disableContinue = (): boolean => {
    if (state.choosenCategory == null || state.choosenSubCat == null) {
      return true
    }

    if (state.showCustomPackages) {
      if (state.choosenSubCat.selectedType === 'SELECT_SERVICE') {
        if (
          state.choosenDataMM?.id === 'c7677ed2-0a6f-4ea0-a050-cff4f25d084f' &&
          state.choosenCallMM == null
        ) {
          return true
        }
        if (
          (state.choosenCallMM?.price === 10000 ||
          state.choosenCallMM?.price === 9000) &&
          state.choosenDataMM === undefined
        ) {
          return true
        }
        if (
          state.choosenDataMM?.price === 15000 &&
          state.choosenCallMM === undefined
        ) {
          return true
        }
        if (
          state.choosenDataMM == null &&
          state.choosenCallMM?.id === '33d15647-aa10-4a7c-92b7-08f6b504a714'
        ) {
          return true
        }
        if (
          state.choosenDataMM === undefined &&
          state.choosenCallMM === undefined
        ) {
          return true
        }
      }
      return false
    }

    if (state.choosenSubCat.selectedType === 'SELECT_SERVICE') {
      if (state.choosenSubCat.id === 'dbd2fe2b-5393-4648-9bba-2e7c5ffa0a59') {
        return state.choosenPackage == null
      }
    } else {
      if (state.choosenPackage == null) {
        return true
      }
    }

    return false
  }

  const getServiceIcon = (type: string) => {
    //  if(theme === 'dark'){
    //   return `/icons/package_${type}_dark.svg`
    // }else{
    //   return `/icons/package_${type}.svg`
    // }
    switch (type) {
      case 'data':
        return (theme === 'dark'
          ? '/icons/package_data_dark.svg'
          : '/icons/package_data.svg')
      case 'call':
        return (theme === 'dark'
          ? '/icons/package_call_dark.svg'
          : '/icons/package_call.svg')
      case 'sms':
        return (theme === 'dark'
          ? '/icons/package_sms_dark.svg'
          : '/icons/package_sms.svg')
      case 'video':
        return '/icons/entertainment.svg'
      default:
        return '/icons/service.svg'
    }
  }

  const handleChoosePackage = (
    packageI: PackageDetailInfo,
    services: serviceDetail
  ): void => {
    const updatedCall =
      packageI.name === 'Call' || packageI.name === 'Яриа'
        ? state.choosenCallMM === services
          ? undefined
          : services
        : state.choosenCallMM

    const updatedData =
      packageI.name === 'Data' || packageI.name === 'Дата'
        ? state.choosenDataMM === services
          ? undefined
          : services
        : state.choosenDataMM

    const updatedSms =
      packageI.name === 'Мессеж' || packageI.name === 'Message'
        ? state.choosenSmsMM === services
          ? undefined
          : services
        : state.choosenSmsMM

    const updatedVideo =
      packageI.name === 'Video streaming' ||
        packageI.name === 'Видео стриминг үйлчилгээ'
        ? state.choosenVideoStreamingMM === services
          ? undefined
          : services
        : state.choosenVideoStreamingMM

    let updatedEntertainment = state.choosenEntertainmentMM ?? []
    if (
      packageI.name === 'Энтертайнмент дата багцууд' ||
      packageI.name === 'Entertainment data packages'
    ) {
      const alreadySelected = updatedEntertainment.some(
        (s) => s.id === services.id
      )
      updatedEntertainment = alreadySelected
        ? updatedEntertainment.filter((s) => s.id !== services.id)
        : [...updatedEntertainment, services]
    }
    setState({
      choosenPackage: undefined,
      choosenCallMM: updatedCall,
      choosenDataMM: updatedData,
      choosenSmsMM: updatedSms,
      choosenVideoStreamingMM: updatedVideo,
      choosenEntertainmentMM: updatedEntertainment
    })
    if (onSelectionChange != null) {
      onSelectionChange({
        choosenPackage:
          state.choosenSubCat?.selectedType === 'SELECT_SERVICE'
            ? undefined
            : state.choosenPackage,
        choosenCallMM: updatedCall,
        choosenDataMM: updatedData,
        choosenSmsMM: updatedSms,
        choosenVideoStreamingMM: updatedVideo,
        choosenEntertainmentMM: updatedEntertainment
      })
    }
  }

  const handleSavePackage = async (): Promise<void> => {
    trackNewNumberEvent('click_cta', {
      step_name: 'registration_info',
      sub_step_name: 'package',
      button_name: 'continue'
    })
    setState({ loading: true })
    let req: SavePackageInfoReq

    const addonPackages = [
      ...((state.choosenSmsMM != null)
        ? [{ id: state.choosenSmsMM.id ?? '', name: state.choosenSmsMM.name ?? '', isAdditionalService: true }]
        : []),
      ...((state.choosenVideoStreamingMM != null)
        ? [{ id: state.choosenVideoStreamingMM.id ?? '', name: state.choosenVideoStreamingMM.name ?? '', isAdditionalService: true }]
        : []),
      ...(state.choosenEntertainmentMM?.map(ent => ({
        id: ent.id ?? '',
        name: ent.name ?? '',
        isAdditionalService: true
      })) ?? [])
    ]

    if (state.choosenPackage != null) {
      const plan = state.choosenPackage
      const planServices: any[] = []

      if (plan?.data != null) {
        planServices.push({
          id: plan.data.id ?? '',
          name: plan.data.name ?? ''
        })
      }

      if (plan?.call != null) {
        planServices.push({
          id: plan.call.id ?? '',
          name: plan.call.name ?? ''
        })
      }

      req = {
        instanceId,
        rateplan: plan.rateplan ?? state.choosenSubCat?.rateplan ?? '',
        category: {
          categoryId: state.choosenCategory?.categoryId ?? '',
          categoryName: state.choosenCategory?.categoryName ?? ''
        },
        subCategory: {
          id: state.choosenSubCat?.id ?? '',
          name: state.choosenSubCat?.name ?? ''
        },
        packages: [
          plan,
          ...planServices,
          ...addonPackages
        ],
        referral: {
          referralCode: referralCode ?? '',
          promoCode: 'REFERRAL-PROMO'
        }
      }
    } else {
      // ✅ CUSTOM PACKAGE MODE (SELECT_SERVICE)
      const customPackages: any[] = []

      if (state.choosenDataMM != null) {
        customPackages.push({
          id: state.choosenDataMM.id ?? '',
          name: state.choosenDataMM.name ?? ''
        })
      }

      if (state.choosenCallMM != null) {
        customPackages.push({
          id: state.choosenCallMM.id ?? '',
          name: state.choosenCallMM.name ?? ''
        })
      }

      if (state.choosenSmsMM != null) {
        customPackages.push({
          id: state.choosenSmsMM.id ?? '',
          name: state.choosenSmsMM.name ?? '',
          isAdditionalService: true
        })
      }

      if (state.choosenVideoStreamingMM != null) {
        customPackages.push({
          id: state.choosenVideoStreamingMM.id ?? '',
          name: state.choosenVideoStreamingMM.name ?? '',
          isAdditionalService: true
        })
      }

      if ((state.choosenEntertainmentMM?.length ?? 0) > 0) {
        state.choosenEntertainmentMM?.forEach(s => {
          customPackages.push({
            id: s.id ?? '',
            name: s.name ?? '',
            isAdditionalService: true
          })
        })
      }

      req = {
        instanceId,
        rateplan: state.choosenSubCat?.rateplan ?? '',
        category: {
          categoryId: state.choosenCategory?.categoryId ?? '',
          categoryName: state.choosenCategory?.categoryName ?? ''
        },
        subCategory: {
          id: state.choosenSubCat?.id ?? '',
          name: state.choosenSubCat?.name ?? ''
        },
        packages: customPackages,
        referral: {
          referralCode: referralCode ?? '',
          promoCode: 'REFERRAL-PROMO'
        }
      }
    }

    try {
      const res = await numberv2.savePackageInfo(
        req,
        isWebView ? 'mobicom_app' : 'mobicom_web'
      )

      if ((res == null) || res.error) {
        trackNewNumberEvent('step_error', {
          step_name: 'registration_info',
          sub_step_name: 'package',
          error_type: normalizeTrackingText(res?.error ?? 'save_package_failed')
        })
        showError(
          locale === 'en'
            ? res?.cause?.messageEn ?? res?.error ?? translate('request.failed')
            : res?.cause?.messageMn ?? res?.error ?? translate('request.failed')
        )
        return
      }

      next({
        rateplan: res.rateplan,
        choosenPackage:
          state.choosenCategory != null &&
          getCategoryKey(state.choosenCategory) === 'prepaid' &&
          state.choosenSubCat?.name === 'Stars'
            ? getStarsPackageSummary(state.choosenPackage?.name)
            : (state.choosenSubCat?.name ?? '') +
              ' - ' +
              (state.choosenSubCat?.selectedType === 'SELECT_SERVICE'
                ? [
                    (state.choosenCallMM != null)
                      ? `${state.choosenCallMM.name} ${translate('mymobi.unlimited')}`
                      : undefined,
                    state.choosenDataMM?.name,
                    state.choosenPackage?.name
                  ].filter(Boolean).join(' / ')
                : state.choosenPackage?.name ?? ''),
        choosenInfo: {
          choosenPackage: state.choosenPackage,
          choosenCallMM: state.choosenCallMM ?? state.choosenPackage?.call,
          choosenDataMM: state.choosenDataMM ?? state.choosenPackage?.data,
          choosenSmsMM: state.choosenSmsMM,
          choosenVideoStreamingMM: state.choosenVideoStreamingMM,
          choosenEntertainmentMM: state.choosenEntertainmentMM
        }
      })
    } catch (e) {
      trackNewNumberEvent('step_error', {
        step_name: 'registration_info',
        sub_step_name: 'package',
        error_type: 'save_package_failed'
      })
      showError(translate('request.failed'))
    } finally {
      setState({ loading: false })
    }
  }

  const handleShowCustomPackages = (): void => {
    setState({
      showCustomPackages: !state.showCustomPackages,
      choosenPackage: !state.showCustomPackages ? undefined : state.choosenPackage
    })

    if (!state.showCustomPackages) {
      onSelectionChange?.({})
    }
  }

  const handleChooseAddon = (services: serviceDetail): void => {
    const isSmsAddon =
      services.name.toLowerCase().includes('мессеж') ||
      services.name.toLowerCase().includes('message')

    if (isSmsAddon) {
      const isSame = state.choosenSmsMM?.id === services.id
      const updatedSms = isSame ? undefined : services

      setState({
        ...state,
        choosenSmsMM: updatedSms
      })

      onSelectionChange?.({
        ...state,
        choosenPackage: state.choosenPackage,
        choosenSmsMM: updatedSms
      })

      return
    }

    let updatedEntertainmentMM =
      state.choosenEntertainmentMM != null
        ? [...state.choosenEntertainmentMM]
        : []

    const exists = updatedEntertainmentMM.some((x) => x.id === services.id)

    if (exists) {
      updatedEntertainmentMM = updatedEntertainmentMM.filter(
        (x) => x.id !== services.id
      )
    } else {
      updatedEntertainmentMM.push(services)
    }

    setState({
      ...state,
      choosenEntertainmentMM: updatedEntertainmentMM
    })

    onSelectionChange?.({
      ...state,
      choosenPackage: state.choosenPackage,
      choosenEntertainmentMM: updatedEntertainmentMM
    })
  }

  if (state.package === undefined && state.loading === true) {
    return (
      <div className='flex items-center justify-center w-full'>
        <Loader />
      </div>
    )
  }

  return (
    <div className='flex flex-col items-start justify-start w-full gap-4 p-6 bg-surfaceMid rounded-xl md:p-8'>
      <div
        ref={categoryScrollRef}
        className='w-full overflow-x-auto overflow-y-hidden no-scrollbar pb-1 cursor-grab active:cursor-grabbing select-none'
        style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x' }}
        onMouseDown={handleCategoryMouseDown}
        onMouseMove={handleCategoryMouseMove}
        onMouseUp={stopCategoryDragging}
        onMouseLeave={stopCategoryDragging}
      >
        <div className='flex min-w-max items-center gap-4 pr-6'>
          {orderedCategories.map((item, i) => (
            <div key={i} className='shrink-0 snap-start'>
              <div
                className={
                  'rounded-full cursor-pointer flex justify-center items-center font-bold px-8 py-2 border whitespace-nowrap transition-colors ' +
                  (item === state.choosenCategory
                    ? 'bg-secondary text-textInverse'
                    : 'border-secondaryMidStroke bg-surfaceInverse text-textHigh')
                }
                onClick={() => handleCategoryClick(item)}
              >
                <span className='text-sm md:text-sm text-center whitespace-nowrap'>
                  {getCategoryLabel(item)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {state.choosenCategory?.subCategories !== undefined &&
        state.choosenCategory?.subCategories.length > 0
        ? (
          <div className='flex flex-col w-full gap-4'>
            <span className='text-sm font-bold text-textMid'>
              {translate('package')}
            </span>
            <TextField
              label={translate('package.choose')}
              variant='outlined'
              className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
              value={state.choosenSubCat?.name ?? ''}
              select
              SelectProps={{
                MenuProps: {
                  sx: { maxHeight: '40%' },
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(var(--surfaceInverse))',
                      color: 'var(--color-text-textHigh)',
                      borderRadius: '8px'
                    }
                  }
                }
              }}
              InputLabelProps={{
                style: { color: 'var(--color-text-textHigh)' }
              }}
              InputProps={{
                style: {
                  color: 'var(--color-text-textHigh)',
                  '&::placeholder': {
                    color: 'var(--color-text-secondaryHighStroke)'
                  }
                } as any
              }}
              sx={{
                '& fieldset': {
                  borderRadius: '8px'
                },
                '& .MuiSelect-icon': {
                  color:
                    theme === 'dark'
                      ? 'var(--color-text-textHigh)'
                      : 'var(--color-text-secondaryHighStroke)'
                }
              }}
            >
              {state.choosenCategory?.subCategories.map((item, i) => (
                <MenuItem
                  key={i}
                  value={item.name}
                  onClick={() => handleSubCategoryClick(item, {
                    autoSelectSinglePackage: !(
                      state.choosenCategory != null &&
                      getCategoryKey(state.choosenCategory) === 'prepaid' &&
                      item.name === 'Stars'
                    )
                  })}
                  className='text-sm bg-surfaceInverse text-textHigh'
                >
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            {state.choosenSubCat?.packages?.some(
              (p) =>
                state.choosenDataMM === p ||
                state.choosenPackage === p ||
                state.choosenSubCat?.selectedType === 'SELECT_SERVICE'
            ) && (
              <span className='text-sm font-bold text-textMid'>
                {translate('mymobi.recommend.package')}
              </span>
            )}
          </div>
          )
        : (
          <></>
          )}

      {state.choosenSubCat?.packages !== undefined &&
        state.choosenSubCat?.packages.length > 0
        ? (
          <div className='flex flex-col w-full gap-3'>
            {state.choosenSubCat?.id === '6bd9c88c-26f6-4d3b-9131-9b22a7578b56'
              ? (
                <div className='grid w-full grid-cols-2 gap-1 overflow-auto'>
                  {state.choosenSubCat.packages.map((item, i) => (
                    <div
                      key={i}
                      onClick={
                        state.choosenPackage === item
                          ? () => {
                              setState({ choosenPackage: undefined })
                              onSelectionChange?.({})
                            }
                          : () => {
                              setState({ choosenPackage: item })
                              onSelectionChange?.({
                                choosenPackage: item,
                                choosenCallMM: undefined,
                                choosenDataMM: undefined,
                                choosenSmsMM: undefined,
                                choosenVideoStreamingMM: undefined,
                                choosenEntertainmentMM: []
                              })
                            }
                      }
                      className={classNames(
                        'border rounded-xl p-3 cursor-pointer flex justify-between items-start bg-surfaceInverse',
                        state.choosenPackage === item
                          ? 'border-secondaryMainStroke'
                          : 'border-secondaryMidStroke'
                      )}
                    >
                      <div className='flex flex-col w-5/6'>
                        <span className='text-lg font-bold text-textHigh'>{item.name}</span>
                        {item.services.map((services, idx) => (
                          <span key={idx} className='text-xs truncate text-textMid'>
                            {services.name}
                          </span>
                        ))}
                        <span className='mt-3 text-sm text-textHigh'>{item.price}₮</span>
                      </div>
                      <Checkbox
                        color='error'
                        icon={<RadioButtonUncheckedOutlined className='text-textHigh' />}
                        checkedIcon={<CheckCircle className='text-primaryMain' />}
                        className='p-0'
                        checked={state.choosenPackage === item}
                      />
                    </div>
                  ))}
                </div>
                )
              : state.choosenSubCat?.selectedType === 'SELECT_SERVICE' &&
                state.choosenSubCat?.id === 'dbd2fe2b-5393-4648-9bba-2e7c5ffa0a59'
                ? (
                  <div className='grid grid-cols-1 md:grid-cols-1 gap-4 w-full'>
                    {state.package?.categories
                      ?.flatMap((cat) => cat.subCategories)
                      ?.find((sub) => sub.name === 'mymobi')
                      ?.packages?.find((pkg) => (pkg.name === 'Багцууд' || pkg.name === 'Bundles'))
                      ?.services?.flat()
                      ?.filter((plan: any) => plan && typeof plan === 'object' && plan.name)
                      ?.map((plan: any, idx: number) => (
                        <div
                          key={idx}
                          className={classNames(
                            'rounded-xl border shadow-sm bg-surfaceInverse cursor-pointer transition transform hover:scale-[1.02] relative',
                            state.choosenPackage?.id === `${plan.data?.id}-${idx}`
                              ? 'border-secondaryMainStroke'
                              : 'border-secondaryMidStroke'
                          )}
                          onClick={() => {
                            if (!plan?.data) return
                            const planUniqueId = `${plan.data?.id}-${idx}`
                            const currentSelectedId = state.choosenPackage?.id
                            const isCurrentlySelected = currentSelectedId === planUniqueId

                            if (isCurrentlySelected) {
                              setState({
                                choosenPackage: undefined,
                                choosenCallMM: undefined,
                                choosenDataMM: undefined,
                                choosenSmsMM: undefined,
                                choosenVideoStreamingMM: undefined,
                                choosenEntertainmentMM: []
                              })
                              onSelectionChange?.({})
                            } else {
                              setState({
                                choosenPackage: {
                                  id: planUniqueId,
                                  name: plan.name ?? '',
                                  price: plan.price ?? 0,
                                  rateplan: state.choosenSubCat?.rateplan ?? '',
                                  originalId: plan.data?.id,
                                  data: plan.data,
                                  call: plan.call,
                                  services: plan.services ?? []
                                },
                                showCustomPackages: false,
                                choosenCallMM: undefined,
                                choosenDataMM: undefined,
                                choosenSmsMM: undefined,
                                choosenVideoStreamingMM: undefined,
                                choosenEntertainmentMM: undefined
                              })
                              onSelectionChange?.({
                                choosenPackage: {
                                  id: planUniqueId,
                                  name: plan.name ?? '',
                                  price: plan.price ?? 0,
                                  rateplan: state.choosenSubCat?.rateplan ?? '',
                                  originalId: plan.data?.id,
                                  data: plan.data ?? undefined,
                                  call: plan.call ?? undefined,
                                  services: plan.services ?? []
                                },
                                choosenCallMM: undefined,
                                choosenDataMM: undefined,
                                choosenSmsMM: undefined,
                                choosenVideoStreamingMM: undefined,
                                choosenEntertainmentMM: []
                              })
                            }
                          }}
                        >
                          {plan.isRecommended && (
                            <div className='absolute -top-3 left-4 bg-primaryMain text-white px-3 py-1 rounded-full text-xs font-semibold z-10'>
                              {translate('mymobi.popular')}
                            </div>
                          )}
                          <div className='px-4 py-2 border-b border-secondaryMidStroke flex items-center justify-between '>
                            <h3 className='text-textHigh font-semibold text-base'>
                              {plan.name ?? 'Unknown'}
                            </h3>
                            <Checkbox
                              color='error'
                              icon={<RadioButtonUncheckedOutlined className='text-textHigh' />}
                              checkedIcon={<CheckCircle className='text-primaryMain' />}
                              className='p-0'
                              checked={state.choosenPackage?.id === `${plan.data?.id}-${idx}`}
                            />
                          </div>

                          <div className='px-4 py-3 flex flex-col gap-1'>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-1'>
                                <Image
                                  width={20}
                                  height={20}
                                  src={getServiceIcon(plan?.data ? 'data' : 'call')}
                                  alt={plan.data?.icon}
                                  className='w-5 h-5 object-contain mr-1'
                                />
                                <span className='font-semibold text-textHigh'>
                                  {plan.data?.name ?? '-'}
                                </span>

                                <span className='text-textHigh text-sm'>{translate('mymobi.data')}</span>
                              </div>
                            </div>

                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-1 w-[80%]'>
                                <Image
                                  width={20}
                                  height={20}
                                  src={getServiceIcon('call')}
                                  alt={plan.data?.icon}
                                  className='w-5 h-5 object-contain mr-1'
                                />
                                <p className='text-textHigh text-sm leading-tight break-words'>
                                  {plan.call?.name ?? '-'}{' '}
                                  <span className='text-primaryMain'>
                                    {translate('mymobi.unlimited')}
                                  </span>
                                </p>
                              </div>

                            </div>

                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-1'>
                                <Image
                                  width={20}
                                  height={20}
                                  src={resolveImageUrl('/uploads/Voo_button.png')}
                                  alt='Voo'
                                  className='w-5 h-5 object-contain mr-1'
                                />
                                <p className='text-textHigh text-sm leading-tight break-words'>
                                  {plan.data?.vooPackage ?? '-'}
                                </p>
                              </div>
                            </div>
                            {
                              plan?.data?.hasSonsy && (
                                <div className='flex items-center justify-between'>
                                  <div className='flex items-center gap-1'>
                                    <Image
                                      draggable={false}
                                      width={20}
                                      height={20}
                                      alt='sonsy'
                                      src={resolveImageUrl('/uploads/sonsy.png')}
                                      className='inline-block rounded-lg mr-1'
                                    />
                                    <p className='text-textHigh text-sm leading-tight break-words'>
                                      sonsy
                                    </p>
                                  </div>
                                  <span className='text-textHigh text-sm'>{translate('mymobi.month')}</span>
                                </div>
                              )
                            }
                            {plan?.data?.hasMobilife && (
                              <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-1'>
                                  <Image
                                    width={20}
                                    height={20}
                                    src={resolveImageUrl('/uploads/shield-check.png')}
                                    alt='mobilife'
                                    className='w-5 h-5 object-contain mr-1'
                                  />
                                  <p className='text-textHigh text-sm leading-tight break-words'>
                                    mobilife
                                  </p>
                                </div>
                                <span className='font-semibold text-lg text-textHigh'>
                                  {moneyFormatter(plan.price ?? 0)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                    <div className='border border-dotted border-red-500 rounded-lg p-2'>
                      <div
                        className='flex flex-col items-center justify-center cursor-pointer transition-colors'
                        onClick={handleShowCustomPackages}
                      >
                        <div className='flex items-center justify-center gap-2'>
                          <Image
                            draggable={false}
                            width={50}
                            height={50}
                            className='w-fit h-fit bg-primaryMain'
                            src={resolveImageUrl('/uploads/icons/book-plus.svg')}
                            alt='book'
                          />
                          <h3 className='text-textHigh font-semibold text-base text-center px-3 py-2'>
                            {translate('mymobi.own.package')}
                          </h3>
                          {state.showCustomPackages
                            ? (
                              <ExpandLess className='text-textMid' />
                              )
                            : (
                              <ExpandMore className='text-textMid' />
                              )}
                        </div>
                      </div>
                      {state.showCustomPackages && state.choosenSubCat?.packages !== undefined &&
                        state.choosenSubCat?.packages.length > 0 && (
                          <div className='flex flex-col w-full gap-3 mt-2'>
                            <div className='w-full h-px bg-gray-300 ' />
                            {(state.choosenSubCat?.id as string) === '6bd9c88c-26f6-4d3b-9131-9b22a7578b56'
                              ? (
                                <div className='grid w-full grid-cols-2 gap-1 overflow-auto'>
                                  {state.choosenSubCat.packages.map((item, i) => (
                                    <div
                                      key={i}
                                      onClick={
                                        state.choosenPackage === item
                                          ? () => {
                                              setState({ choosenPackage: undefined })
                                              onSelectionChange?.({})
                                            }
                                          : () => {
                                              setState({ choosenPackage: item })
                                              onSelectionChange?.({
                                                choosenPackage: item,
                                                choosenCallMM: undefined,
                                                choosenDataMM: undefined,
                                                choosenSmsMM: undefined,
                                                choosenVideoStreamingMM: undefined,
                                                choosenEntertainmentMM: []
                                              })
                                            }
                                      }
                                      className={classNames(
                                        'border rounded-xl p-3 cursor-pointer flex justify-between items-start bg-surfaceInverse',
                                        state.choosenPackage === item
                                          ? 'border-secondaryMainStroke'
                                          : 'border-secondaryMidStroke'
                                      )}
                                    >
                                      <div className='flex flex-col w-5/6'>
                                        <span className='text-lg font-bold text-textHigh'>
                                          {item.name}
                                        </span>
                                        {item.services.map((services, idx) => (
                                          <span key={idx} className='text-xs truncate text-textMid'>
                                            {services.name}
                                          </span>
                                        ))}
                                        <span className='mt-3 text-sm text-textHigh'>
                                          {item.price}₮
                                        </span>
                                      </div>
                                      <Checkbox
                                        color='error'
                                        icon={
                                          <RadioButtonUncheckedOutlined className='text-textHigh' />
                                        }
                                        checkedIcon={<CheckCircle className='text-primaryMain' />}
                                        className='p-0'
                                        checked={state.choosenPackage === item}
                                      />
                                    </div>
                                  ))}
                                </div>
                                )
                              : (
                                  state.choosenSubCat.packages
                                    .filter((item) => item.name !== 'Багцууд' && item.name !== 'Bundles')
                                    .map((item, i) =>
                                      state.choosenSubCat?.selectedType === 'SELECT_SERVICE'
                                        ? (
                                            item.isAdditionalService !== true && (
                                              <div key={i} className='flex flex-col gap-2'>
                                                <span className='text-sm font-bold text-textMid'>
                                                  {item.name}
                                                </span>
                                                {item.name === 'Call' || item.name === 'Яриа'
                                                  ? (
                                                    <div className='flex flex-col gap-2'>
                                                      {item.services.map((services, idx) => (
                                                        <div
                                                          key={idx}
                                                          className={classNames(
                                                            'relative border rounded-xl px-4 py-3 flex justify-between items-center cursor-pointer bg-surfaceInverse overflow-hidden',
                                                            state.choosenCallMM === services ||
                                                          state.choosenDataMM === services ||
                                                          state.choosenSmsMM === services ||
                                                          state.choosenVideoStreamingMM === services ||
                                                          (state.choosenEntertainmentMM ?? []).some(
                                                            (s) => s.id === services.id
                                                          )
                                                              ? 'border-secondaryMainStroke'
                                                              : 'border-secondaryMidStroke'
                                                          )}
                                                          onClick={() => handleChoosePackage(item, services)}
                                                        >
                                                          <div className='flex flex-col items-start gap-1 flex-1'>
                                                            <div className='w-[75%]'>
                                                              <span className='text-sm font-semibold text-textHigh'>
                                                                {services.name}&nbsp;
                                                              </span>
                                                              <span className='text-sm font-medium text-primaryMain'>
                                                                {translate('mymobi.unlimited')}
                                                              </span>
                                                            </div>
                                                            <div className='flex flex-col items-start'>
                                                              {typeof services.vooPackage === 'string' &&
                                                            services.vooPackage.trim() !== '' && (
                                                              <span className='text-xs text-textMid font-normal'>
                                                                {services.vooPackage}
                                                              </span>
                                                              )}
                                                              {services.hasMobiAI === true && (
                                                                <span className='text-xs text-textMid font-normal'>
                                                                  MobiAI
                                                                </span>
                                                              )}
                                                            </div>
                                                            <span className='text-xs text-textMid'>
                                                              {moneyFormatter(services.price)}
                                                            </span>
                                                          </div>
                                                          <div className='flex items-center gap-2'>
                                                            {services.is5GSupported === true && (
                                                              <div className='bg-primaryMain text-textAdditional px-2 py-1 rounded text-xs font-semibold'>
                                                                5G
                                                              </div>
                                                            )}
                                                            <Checkbox
                                                              color='error'
                                                              icon={
                                                                <RadioButtonUncheckedOutlined className='text-textHigh' />
                                                          }
                                                              checkedIcon={
                                                                <CheckCircle className='text-primaryMain' />
                                                          }
                                                              className='p-0'
                                                              checked={
                                                            state.choosenCallMM === services ||
                                                            state.choosenDataMM === services ||
                                                            state.choosenSmsMM === services ||
                                                            state.choosenVideoStreamingMM === services ||
                                                            (state.choosenEntertainmentMM ?? []).some(
                                                              (s) => s.id === services.id
                                                            )
                                                          }
                                                            />
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                    )
                                                  : (
                                                    <div className='grid grid-cols-2 gap-1'>
                                                      {item.services.map((services, idx) => (
                                                        <div
                                                          key={idx}
                                                          className={classNames(
                                                            'relative border rounded-xl px-2 py-3 flex justify-between items-start cursor-pointer bg-surfaceInverse overflow-hidden',
                                                            state.choosenCallMM === services ||
                                                          state.choosenDataMM === services ||
                                                          state.choosenSmsMM === services ||
                                                          state.choosenVideoStreamingMM === services ||
                                                          (state.choosenEntertainmentMM ?? []).some(
                                                            (s) => s.id === services.id
                                                          )
                                                              ? 'border-secondaryMainStroke'
                                                              : 'border-secondaryMidStroke'
                                                          )}
                                                          onClick={() => {
                                                            setSelected(services)
                                                            handleChoosePackage(item, services)
                                                          }}
                                                        >
                                                          <div className='flex flex-col w-5/6 gap-2 justify-between h-full'>
                                                            <div className='flex flex-col items-start gap-1'>
                                                              <span
                                                                className={classNames(
                                                                  'text-textHigh',
                                                                  item.name === 'Data' || item.name === 'Дата'
                                                                    ? 'font-bold text-lg'
                                                                    : 'text-sm'
                                                                )}
                                                              >
                                                                {services.name}
                                                              </span>
                                                              <div className='flex flex-col items-start'>
                                                                {typeof services.vooPackage === 'string' &&
                                                              services.vooPackage.trim() !== '' && (

                                                                <span className='text-xs text-textMid font-normal'>
                                                                  {services.vooPackage}
                                                                </span>

                                                                )}
                                                                {services.hasMobiAI === true && (
                                                                  <span className='text-xs text-textMid font-normal'>
                                                                    MobiAI
                                                                  </span>
                                                                )}

                                                                {services.hasSonsy === true && (

                                                                  <span className='text-xs text-textMid font-normal'>
                                                                    sonsy
                                                                  </span>
                                                                )}
                                                                {services.hasMobilife && (
                                                                  <div className='flex gap-1 items-center'>
                                                                    <span className='text-xs text-textMid font-normal'>
                                                                      mobilife
                                                                    </span>
                                                                  </div>
                                                                )}
                                                              </div>

                                                            </div>
                                                            <span className='text-xs text-textMid'>
                                                              {moneyFormatter(services.price)}
                                                            </span>
                                                          </div>
                                                          <Checkbox
                                                            color='error'
                                                            icon={<RadioButtonUncheckedOutlined className='text-textHigh' />}
                                                            checkedIcon={<CheckCircle className='text-primaryMain' />}
                                                            className='p-0'
                                                            checked={
                                                          state.choosenCallMM === services ||
                                                          state.choosenDataMM === services ||
                                                          state.choosenSmsMM === services ||
                                                          state.choosenVideoStreamingMM === services ||
                                                          (state.choosenEntertainmentMM ?? []).some((s) => s.id === services.id)
                                                        }
                                                            onChange={() => handleChoosePackage(item, services)}
                                                          />

                                                          {services.is5GSupported === true && (
                                                            <div className='absolute bottom-0 right-0 bg-primaryMain rounded-tl-xl px-2'>
                                                              <span className='text-textAdditional text-xs font-semibold truncate'>
                                                                5G
                                                              </span>
                                                            </div>
                                                          )}

                                                        </div>
                                                      ))}
                                                      {selected && (
                                                        <div className='col-span-2 w-full mt-4 bg-surfaceInverse border border-dotted border-red-500 rounded-xl p-5 shadow-sm animate-fade-in'>
                                                          <div className='flex flex-col w-full gap-3'>
                                                            <div className='flex flex-wrap items-center justify-between w-full'>
                                                              <div className='flex items-center gap-2 flex-wrap'>
                                                                <h3 className='text-lg font-bold text-textHigh flex items-center gap-1'>
                                                                  {selected.name}
                                                                  <span className='text-sm font-bold text-textHigh'>{translate('mymobi.data')}</span>
                                                                  {(selected.vooPackage || selected.hasMobiAI || selected.hasMobilife) && (
                                                                    <span className='text-textHigh text-lg font-semibold'>+</span>
                                                                  )}
                                                                  {selected.vooPackage && (
                                                                    <Image
                                                                      draggable={false}
                                                                      width={25}
                                                                      height={25}
                                                                      alt='Voo'
                                                                      src={resolveImageUrl('/uploads/Voo_button.png')}
                                                                      className='inline-block'
                                                                    />
                                                                  )}
                                                                  {selected.hasMobilife && (
                                                                    <Image
                                                                      draggable={false}
                                                                      width={25}
                                                                      height={25}
                                                                      alt='mobilife'
                                                                      src={resolveImageUrl('/uploads/shield-check.png')}
                                                                      className='inline-block ml-1'
                                                                    />
                                                                  )}
                                                                  {selected.hasSonsy && (
                                                                    <Image
                                                                      draggable={false}
                                                                      width={25}
                                                                      height={25}
                                                                      alt='Sonsy'
                                                                      src={resolveImageUrl('/uploads/sonsy.png')}
                                                                      className='inline-block ml-1 rounded-lg'
                                                                    />
                                                                  )}
                                                                </h3>
                                                              </div>

                                                              <span className='text-textHigh text-sm font-semibold whitespace-nowrap'>
                                                                {moneyFormatter(selected.price)}
                                                              </span>
                                                            </div>
                                                            <div className='flex flex-wrap items-center justify-between w-full mt-1 gap-3'>
                                                              <p className='text-textMid text-sm leading-snug flex-1 max-w-[80%]'>
                                                                {selected.description ?? ''}
                                                              </p>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                    )}
                                              </div>

                                            )
                                          )
                                        : (
                                          <div
                                            key={i}
                                            onClick={
                                            state.choosenPackage === item
                                              ? () => {
                                                  setState({ choosenPackage: undefined })
                                                  onSelectionChange?.({})
                                                }
                                              : () => {
                                                  setState({ choosenPackage: item })
                                                  onSelectionChange?.({
                                                    choosenPackage: item,
                                                    choosenCallMM: undefined,
                                                    choosenDataMM: undefined,
                                                    choosenSmsMM: undefined,
                                                    choosenVideoStreamingMM: undefined,
                                                    choosenEntertainmentMM: []
                                                  })
                                                }
                                          }
                                            className={classNames(
                                              'border rounded-xl p-3 cursor-pointer flex justify-between items-start bg-surfaceInverse',
                                              state.choosenPackage === item
                                                ? 'border-secondaryMainStroke'
                                                : 'border-secondaryMidStroke'
                                            )}
                                          >
                                            <div className='flex flex-col w-5/6'>
                                              <span className='text-lg font-bold text-textHigh'>
                                                {item.name}
                                              </span>
                                              <p className='text-xs truncate text-textMid'>
                                                {item.services.map((services, idx) => (
                                                  <span key={idx}>
                                                    {services.name}
                                                    {idx < item.services.length - 1 && <br />}
                                                  </span>
                                                ))}
                                              </p>
                                              <span className='mt-3 text-sm text-textHigh'>
                                                {item.price}₮
                                              </span>
                                            </div>
                                            <Checkbox
                                              color='error'
                                              icon={
                                                <RadioButtonUncheckedOutlined className='text-textHigh' />
                                            }
                                              checkedIcon={<CheckCircle className='text-primaryMain' />}
                                              className='p-0'
                                              checked={state.choosenPackage === item}
                                            />
                                          </div>
                                          )
                                    )
                                )}
                          </div>
                      )}
                    </div>
                    {state.choosenSubCat?.selectedType === 'SELECT_SERVICE' &&
                      addonPackages !== undefined &&
                      addonPackages.length > 0 && (
                        <div className='mt-6 w-full border border-secondarySoftLow p-4 rounded-xl bg-surfaceInverse'>
                          <div className='flex flex-col items-start gap-1 pb-4'>
                            <span className='text-base font-bold text-textHigh'>
                              {translate('package.addOnPackage')}
                            </span>
                            <span className='text-xs text-textMid font-medium'>
                              {translate('package.addOnPackageDescription')}
                            </span>
                          </div>
                          <div className='relative flex items-start justify-start w-full'>
                            <div
                              ref={scrollRef}
                              className='flex gap-2 overflow-x-auto no-scrollbar'
                            >
                              {addonPackages.map((addon, index) => (
                                <div
                                  key={index}
                                  className={classNames(
                                    'flex-shrink-0 px-3 py-1 rounded-full cursor-pointer',
                                    selectedAddonTab === index
                                      ? 'bg-secondary text-textInverse'
                                      : 'bg-surfaceInverse text-textHigh border border-secondaryMidStroke'
                                  )}
                                  onClick={() => setSelectedAddonTab(index)}
                                >
                                  <span className='text-sm font-semibold'>
                                    {addon?.name == 'Мессэж' ? 'Мессеж' : addon?.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className='flex flex-col gap-2 mt-3'>
                            <div className='grid grid-cols-2 gap-1'>
                              {addonPackages[selectedAddonTab]?.services.map(
                                (services, idx) => (
                                  <div
                                    key={idx}
                                    className={classNames(
                                      'border rounded-xl flex flex-col justify-between items-start cursor-pointer bg-surfaceInverse',
                                      state.choosenCallMM === services ||
                                        state.choosenDataMM === services ||
                                        state.choosenSmsMM === services ||
                                        state.choosenVideoStreamingMM === services ||
                                        (state.choosenEntertainmentMM ?? []).some(
                                          (s) => s.id === services.id
                                        )
                                        ? 'border-secondaryMainStroke'
                                        : 'border-secondaryMidStroke'
                                    )}
                                    onClick={() => {
                                      const isPrebuilt = !(state.choosenPackage == null)
                                      if (isPrebuilt) {
                                        handleChooseAddon(services)
                                      } else {
                                        handleChoosePackage(addonPackages[selectedAddonTab], services)
                                      }
                                    }}
                                  >
                                    <div className='gap-2 flex flex-col p-2 justify-between w-full h-full items-start'>
                                      <div className='justify-between items-start flex w-full'>
                                        <div className='flex w-6 h-6 rounded-2xl border border-secondarySoftLow items-center justify-center overflow-hidden'>
                                          <img
                                            src={services.img}
                                            alt='icon'
                                            className='w-full h-full object-cover'
                                          />
                                        </div>
                                        <div
                                          className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors duration-300 ${state.choosenCallMM === services ||
                                            state.choosenDataMM === services ||
                                            state.choosenSmsMM === services ||
                                            state.choosenVideoStreamingMM === services ||
                                            (state.choosenEntertainmentMM ?? []).some(
                                              (s) => s.id === services.id
                                            )
                                            ? 'bg-primaryMain'
                                            : 'bg-secondarySoftLow'
                                            }`}
                                        >
                                          <div
                                            className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${state.choosenCallMM === services ||
                                              state.choosenDataMM === services ||
                                              state.choosenSmsMM === services ||
                                              state.choosenVideoStreamingMM ===
                                              services ||
                                              (state.choosenEntertainmentMM ?? []).some(
                                                (s) => s.id === services.id
                                              )
                                              ? 'translate-x-4'
                                              : 'translate-x-0'
                                              }`}
                                          />
                                        </div>
                                      </div>
                                      <div className='flex justify-center items-center'>
                                        <span className='text-textHigh text-xs font-bold'>
                                          {services.name}
                                        </span>
                                      </div>
                                    </div>
                                    <div className='flex h-10 p-2 bg-surfaceLow rounded-b-xl justify-between items-center w-full'>
                                      {services.dataVolume && (
                                        <span className='text-textHigh text-base font-bold '>
                                          {services.dataVolume}
                                        </span>
                                      )}
                                      <span className='text-textMid text-sm font-medium'>
                                        {(services.price ?? 0)
                                          .toString()
                                          .replace(/\B(?=(\d{3})+(?!\d))/g, '’')}
                                        ₮
                                      </span>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                    )}
                  </div>
                  )
                : (
                    state.choosenSubCat.packages
                      .filter((pkg) => pkg.isAdditionalService !== true)
                      .map((item, i) => (
                        state.choosenCategory != null &&
                        getCategoryKey(state.choosenCategory) === 'prepaid' &&
                        state.choosenSubCat?.name === 'Stars'
                          ? (
                            <div
                              key={i}
                              onClick={
                                state.choosenPackage === item
                                  ? () => {
                                      setState({ choosenPackage: undefined, choosenSmsMM: undefined })
                                      onSelectionChange?.({})
                                    }
                                  : () => {
                                      setState({ choosenPackage: item })
                                      onSelectionChange?.({
                                        choosenPackage: item,
                                        choosenCallMM: undefined,
                                        choosenDataMM: undefined,
                                        choosenSmsMM: undefined,
                                        choosenVideoStreamingMM: undefined,
                                        choosenEntertainmentMM: []
                                      })
                                    }
                              }
                              className={classNames(
                                'border rounded-3xl px-4 py-3 cursor-pointer bg-surfaceInverse flex flex-col gap-3',
                                state.choosenPackage === item
                                  ? 'border-primaryMain'
                                  : 'border-secondaryMidStroke'
                              )}
                            >
                              {(() => {
                                const starsCard = getStarsCardData(item)

                                return (
                                  <>
                              <div className='flex items-start justify-between gap-3'>
                                <div className='flex items-center gap-2 min-w-0'>
                                  <Image
                                    draggable={false}
                                    width={64}
                                    height={64}
                                    src={starsCard.imageSrc}
                                    alt='Stars'
                                    className='w-14 h-14 md:w-28 md:h-28 rounded-2xl object-cover shrink-0'
                                  />
                                  <div className='flex flex-col justify-center gap-2 min-w-0'>
                                    <div className='flex flex-col gap-1'>
                                      <div className='flex items-end gap-2 leading-none'>
                                        <span className='text-[22px] md:text-[32px] font-bold text-[#F44336]'>
                                          {item.name}
                                        </span>
                                        <span className='text-[18px] md:text-[28px] font-bold text-[#78CDD1]'>
                                          {starsPackageLabel}
                                        </span>
                                      </div>
                                      <div className='flex items-end gap-1'>
                                        <span className='w-4 h-4 rounded-md bg-[#78CDD1] shrink-0' />
                                        <span className='w-4 h-4 bg-[#FFD996] shrink-0' />
                                        <span className='w-0 h-0 border-l-[16px] border-l-[#F16C7A] border-t-[16px] border-t-transparent shrink-0' />
                                      </div>
                                    </div>
                                    <span className='text-lg leading-6 md:text-lg md:leading-8 text-textHigh'>
                                      {starsCard.description}
                                    </span>
                                  </div>
                                </div>
                                <Checkbox
                                  color='error'
                                  icon={<RadioButtonUncheckedOutlined className='text-textHigh' />}
                                  checkedIcon={<CheckCircle className='text-primaryMain' />}
                                  className='p-0 shrink-0'
                                  checked={state.choosenPackage === item}
                                />
                              </div>
                              <div className='w-full h-px bg-secondaryMidStroke' />
                              <div className='flex flex-col gap-2'>
                                {starsCard.features.map((feature, featureIndex) => (
                                  <div key={featureIndex} className='flex items-start gap-3'>
                                    <span className='text-primaryMain text-lg leading-6 shrink-0'>✓</span>
                                    <span className='text-sm md:text-base leading-6 text-textHigh'>
                                      {feature}
                                    </span>
                                  </div>
                                ))}
                              </div>
                                  </>
                                )
                              })()}
                            </div>
                            )
                          : (
                            <div
                              key={i}
                              onClick={
                                state.choosenPackage === item
                                  ? () => {
                                      setState({ choosenPackage: undefined, choosenSmsMM: undefined })
                                      onSelectionChange?.({})
                                    }
                                  : () => {
                                      setState({ choosenPackage: item })
                                      onSelectionChange?.({
                                        choosenPackage: item,
                                        choosenCallMM: undefined,
                                        choosenDataMM: undefined,
                                        choosenSmsMM: undefined,
                                        choosenVideoStreamingMM: undefined,
                                        choosenEntertainmentMM: []
                                      })
                                    }
                              }
                              className={classNames(
                                'border rounded-xl p-3 cursor-pointer flex justify-between items-start bg-surfaceInverse',
                                state.choosenPackage === item
                                  ? 'border-secondaryMainStroke'
                                  : 'border-secondaryMidStroke'
                              )}
                            >
                              <div className='flex flex-col w-5/6'>
                                <span className='text-lg font-bold text-textHigh'>{item.name}</span>
                                <p className='text-xs truncate text-textMid'>
                                  {item.services.map((services, idx) => (
                                    <span key={idx}>
                                      {services.name}
                                      {idx < item.services.length - 1 && <br />}
                                    </span>
                                  ))}
                                </p>
                                <span className='mt-3 text-sm text-textHigh'>{item.price}₮</span>
                              </div>
                              <Checkbox
                                color='error'
                                icon={<RadioButtonUncheckedOutlined className='text-textHigh' />}
                                checkedIcon={<CheckCircle className='text-primaryMain' />}
                                className='p-0'
                                checked={
                                  state.choosenPackage === item ||
                                  (state.choosenSubCat?.id ===
                                    '24aa6cc9-71cf-49ac-9c5b-136c93b1f147' &&
                                    i === 0)
                                }
                              />
                            </div>
                            )
                      ))
                  )}
          </div>

          )
        : (
          <></>
          )}
      {state.choosenCategory !== undefined &&
        state.choosenSubCat !== undefined
        ? (
          <div className='flex justify-end w-full'>
            <DarkButton
              onClick={() => {
                void handleSavePackage()
              }}
              disabled={disableContinue()}
            >
              {translate('choose')}
            </DarkButton>
          </div>
          )
        : null}
    </div>
  )
}
export default ChoosePackage
